import React from "react";
import { X, UserPlus, Mail, Shield, Lock } from "lucide-react";
import { SiteComboBox } from "Components/SiteComboBox";
import type { Site, NewUser } from "../../types/organisation";
import type { CreateUserRequest } from "../../types/user";

interface AddUserDialogProps {
  isOpen: boolean;
  newUser: NewUser;
  setNewUser: React.Dispatch<React.SetStateAction<NewUser>>;
  sites: Site[];
  onSave: (payload: CreateUserRequest) => void;
  onClose: () => void;
}

export function AddUserDialog({
  isOpen,
  newUser,
  setNewUser,
  sites,
  onSave,
  onClose,
}: AddUserDialogProps) {
  const [validationError, setValidationError] = React.useState<string | null>(null);

  if (!isOpen) return null;

  function handleSave(): void {
    setValidationError(null);

    const currentUser = newUser;
    const username = (currentUser.username ?? "").toString().trim();
    const password = (currentUser.password ?? "").toString();
    const role = currentUser.role;

    if (!role) {
      setValidationError("Le rôle est requis.");
      return;
    }

    const sitesArr: number[] = Array.isArray(currentUser.sites)
      ? currentUser.sites.map((site) => (typeof site === "number" ? site : Number(site)))
      : currentUser.site
        ? [Number(currentUser.site)]
        : [];

    if (!username || !password || !role || sitesArr.length === 0) {
      setValidationError(
        "Veuillez remplir tous les champs requis (nom, mot de passe, rôle, sites)."
      );
      return;
    }

    const valid = sitesArr.every((id) => sites.some((s) => Number(s.id) === id));
    if (!valid) {
      setValidationError("Site sélectionné invalide.");
      return;
    }

    const payload: CreateUserRequest = {
      username,
      password,
      role,
      sites: sitesArr,
      email: (currentUser.email ?? "").toString().trim() || "",
      first_name: (currentUser.firstName ?? "").toString().trim() || "",
      last_name: (currentUser.lastName ?? "").toString().trim() || "",
    };

    onSave(payload);
  }

  const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: "10px 14px",
    border: "2px solid #e2e8f0",
    borderRadius: "10px",
    fontSize: "14px",
    outline: "none",
    transition: "border-color 0.2s ease, box-shadow 0.2s ease",
    backgroundColor: "#f8fafc",
    color: "#1e293b",
    boxSizing: "border-box",
  };

  const labelStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "13px",
    fontWeight: "600",
    color: "#475569",
    marginBottom: "6px",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement>) => {
    e.target.style.borderColor = "#6366f1";
    e.target.style.boxShadow = "0 0 0 3px rgba(99, 102, 241, 0.1)";
    e.target.style.backgroundColor = "#fff";
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement>) => {
    e.target.style.borderColor = "#e2e8f0";
    e.target.style.boxShadow = "none";
    e.target.style.backgroundColor = "#f8fafc";
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(15, 23, 42, 0.4)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 100,
        animation: "addUserFadeIn 0.2s ease-out",
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <style>{`
        @keyframes addUserFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes addUserSlideUp {
          from { opacity: 0; transform: translateY(16px) scale(0.97); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>

      <div
        style={{
          background: "white",
          borderRadius: "16px",
          boxShadow: "0 20px 60px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.05)",
          width: "90%",
          maxWidth: "520px",
          maxHeight: "90vh",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          animation: "addUserSlideUp 0.25s ease-out",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "20px 24px",
            borderBottom: "1px solid #f1f5f9",
            background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
            color: "white",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <UserPlus size={20} />
            <h3 style={{ margin: 0, fontSize: "17px", fontWeight: "600" }}>
              Ajouter un utilisateur
            </h3>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "rgba(255,255,255,0.2)",
              border: "none",
              borderRadius: "8px",
              padding: "6px",
              cursor: "pointer",
              color: "white",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "background 0.15s",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.3)")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.2)")}
          >
            <X size={18} />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: "24px", overflowY: "auto", flex: 1 }}>
          <form autoComplete="off">
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "16px",
              }}
            >
              <div>
                <label style={labelStyle}>
                  <UserPlus size={14} color="#6366f1" />
                  Nom d'utilisateur *
                </label>
                <input
                  type="text"
                  name="user-name-field"
                  value={newUser.username ?? ""}
                  onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                  placeholder="Entrer le nom d'utilisateur"
                  style={inputStyle}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                  autoComplete="off"
                />
              </div>

              <div>
                <label style={labelStyle}>
                  <Mail size={14} color="#6366f1" />
                  Email
                </label>
                <input
                  type="email"
                  name="user-email-field"
                  value={newUser.email ?? ""}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  placeholder="Entrer l'email"
                  style={inputStyle}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                  autoComplete="off"
                />
              </div>

              <div>
                <label style={labelStyle}>Prénom</label>
                <input
                  type="text"
                  name="user-first-name-field"
                  value={newUser.firstName ?? ""}
                  onChange={(e) => setNewUser({ ...newUser, firstName: e.target.value })}
                  placeholder="Entrer le prénom"
                  style={inputStyle}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                  autoComplete="off"
                />
              </div>

              <div>
                <label style={labelStyle}>Nom</label>
                <input
                  type="text"
                  name="user-last-name-field"
                  value={newUser.lastName ?? ""}
                  onChange={(e) => setNewUser({ ...newUser, lastName: e.target.value })}
                  placeholder="Entrer le nom"
                  style={inputStyle}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                  autoComplete="off"
                />
              </div>

              <div style={{ gridColumn: "1 / -1" }}>
                <label style={labelStyle}>
                  <Lock size={14} color="#6366f1" />
                  Mot de passe *
                </label>
                <input
                  type="password"
                  name="user-password-field"
                  value={newUser.password ?? ""}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  placeholder="Entrer le mot de passe"
                  style={inputStyle}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                  autoComplete="new-password"
                />
              </div>

              <div style={{ gridColumn: "1 / -1" }}>
                <label style={labelStyle}>Sites *</label>
                <SiteComboBox
                  isMulti={true}
                  value={(newUser.sites || []) as number[]}
                  onChange={(selectedSites) => {
                    const sitesArray = Array.isArray(selectedSites) ? selectedSites : [];
                    setNewUser({
                      ...newUser,
                      sites: sitesArray,
                      site: sitesArray.length > 0 ? sitesArray[0] : undefined,
                    });
                  }}
                  placeholder="Sélectionner les sites..."
                  isClearable={true}
                />
              </div>

              <div>
                <label style={labelStyle}>
                  <Shield size={14} color="#6366f1" />
                  Rôle *
                </label>
                <select
                  value={newUser.role ?? "user"}
                  onChange={(e) => {
                    const newRole = e.target.value;
                    setNewUser((prev) => ({ ...prev, role: newRole }));
                  }}
                  style={{
                    ...inputStyle,
                    cursor: "pointer",
                    appearance: "auto",
                  }}
                  onFocus={handleFocus as any}
                  onBlur={handleBlur as any}
                >
                  <option value="user">User</option>
                  <option value="agent">Agent de saisie</option>
                  <option value="superuser">Super User</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
            </div>
          </form>
        </div>

        {validationError && (
          <div
            style={{
              color: "#b91c1c",
              padding: "8px 24px",
              fontSize: "13px",
              fontWeight: 500,
            }}
          >
            {validationError}
          </div>
        )}

        {/* Footer */}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "10px",
            padding: "16px 24px",
            borderTop: "1px solid #f1f5f9",
            background: "#f8fafc",
          }}
        >
          <button
            onClick={onClose}
            style={{
              padding: "9px 20px",
              border: "2px solid #e2e8f0",
              borderRadius: "10px",
              background: "white",
              color: "#64748b",
              fontSize: "14px",
              fontWeight: "500",
              cursor: "pointer",
              transition: "all 0.15s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = "#cbd5e1";
              e.currentTarget.style.color = "#334155";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = "#e2e8f0";
              e.currentTarget.style.color = "#64748b";
            }}
          >
            Annuler
          </button>
          <button
            onClick={handleSave}
            style={{
              padding: "9px 24px",
              border: "none",
              borderRadius: "10px",
              background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
              color: "white",
              fontSize: "14px",
              fontWeight: "600",
              cursor: "pointer",
              transition: "all 0.15s",
              boxShadow: "0 2px 8px rgba(99, 102, 241, 0.3)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = "0 4px 12px rgba(99, 102, 241, 0.4)";
              e.currentTarget.style.transform = "translateY(-1px)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = "0 2px 8px rgba(99, 102, 241, 0.3)";
              e.currentTarget.style.transform = "translateY(0)";
            }}
          >
            Ajouter
          </button>
        </div>
      </div>
    </div>
  );
}

export default AddUserDialog;
